import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Share2, MessageSquare, ThumbsUp, MessageCircle, ThumbsDown, RotateCcw, ExternalLink, Lock, CheckCircle, Send } from 'lucide-react';
import { useShareVisibilityOptions } from '../lib/shareVisibility';
import { useTranslation } from 'react-i18next';
import { Video, ShareVisibility } from '../types/video';
import { apiClient } from '../lib/apiClient';
import { useVideoReactions } from '../hooks/useVideoReactions';
import CustomVideoPlayer from './CustomVideoPlayer';
import SharedVideoLayout from './SharedVideoLayout';
import Comments from './Comments';
import { toast } from '../lib/toast';
import { copyText } from '../lib/clipboard';
import { getAccessToken } from '../lib/authFetch';
import RichText from './RichText';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://nodebackendforacvrecording-1.onrender.com';

// ─── Math captcha form component ─────────────────────────────────────────────
// Uses a simple arithmetic challenge (a + b = ?) stored in React state.
// This avoids all canvas-timing issues that plagued react-simple-captcha while
// still blocking naive bots. The challenge is generated once on mount.
interface AccessRequestFormProps {
  email:      string;
  onSubmit:   (name: string, message: string) => Promise<void>;
  onBack:     () => void;
  submitting: boolean;
}

const AccessRequestForm: React.FC<AccessRequestFormProps> = ({ email, onSubmit, onBack, submitting }) => {
  const { t } = useTranslation();
  const [name,    setName]    = useState('');
  const [message, setMessage] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaErr,    setCaptchaErr]    = useState(false);

  // Generate challenge once — stored in state so it never changes on re-render.
  const [challenge] = useState(() => {
    const a = Math.floor(Math.random() * 12) + 1;
    const b = Math.floor(Math.random() * 12) + 1;
    return { a, b, answer: a + b };
  });

  const handleSubmit = async () => {
    const userAnswer = parseInt(captchaAnswer.trim(), 10);
    if (isNaN(userAnswer) || userAnswer !== challenge.answer) {
      setCaptchaErr(true);
      setCaptchaAnswer('');
      return;
    }
    setCaptchaErr(false);
    await onSubmit(name, message);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 13px', borderRadius: 8, fontSize: 14,
    border: '1px solid var(--border, #d1d5db)',
    background: 'var(--background, #fff)', color: 'var(--foreground, #111)',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground, #111)', display: 'block', marginBottom: 4 }}
               htmlFor="ra_name">
          {t('shared_video.form_name', 'Nombre completo')} *
        </label>
        <input id="ra_name" type="text" value={name} onChange={e => setName(e.target.value)}
               placeholder={t('shared_video.form_name_placeholder', 'Tu nombre')} style={inputStyle} />
      </div>
      <div>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground, #111)', display: 'block', marginBottom: 4 }}
               htmlFor="ra_email2">
          {t('shared_video.form_email', 'Correo electrónico')}
        </label>
        <input id="ra_email2" type="email" value={email} readOnly
               style={{ ...inputStyle, background: 'var(--surfaceHover, #f3f4f6)', color: 'var(--textSecondary, #6b7280)' }} />
      </div>
      <div>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground, #111)', display: 'block', marginBottom: 4 }}
               htmlFor="ra_message">
          {t('shared_video.form_message', 'Mensaje (opcional)')}
        </label>
        <textarea id="ra_message" value={message} onChange={e => setMessage(e.target.value)} rows={3}
                  placeholder={t('shared_video.form_message_placeholder', '¿Por qué necesitas acceso a este video?')}
                  style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      {/* Math captcha — clear, unambiguous, no canvas timing issues */}
      <div style={{
        padding: '12px 16px', borderRadius: 8,
        background: 'var(--surfaceHover, #f3f4f6)',
        border: `1px solid ${captchaErr ? '#ef4444' : 'var(--border, #d1d5db)'}`,
      }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground, #111)', display: 'block', marginBottom: 8 }}
               htmlFor="ra_captcha">
          {t('shared_video.captcha_question', 'What is')} {challenge.a} + {challenge.b}? *
        </label>
        <input
          id="ra_captcha"
          type="number"
          inputMode="numeric"
          value={captchaAnswer}
          onChange={e => { setCaptchaAnswer(e.target.value); setCaptchaErr(false); }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="0"
          style={{ ...inputStyle, width: 100 }}
        />
        {captchaErr && (
          <span style={{ fontSize: 12, color: '#ef4444', display: 'block', marginTop: 6 }}>
            {t('shared_video.captcha_invalid', 'Respuesta incorrecta. Intenta de nuevo.')}
          </span>
        )}
      </div>

      <button onClick={handleSubmit} disabled={submitting || !name.trim()} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '11px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
        background: 'var(--primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)', border: 'none',
        cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1,
      }}>
        <Send size={14} aria-hidden />
        {submitting ? t('shared_video.sending', 'Enviando...') : t('shared_video.send_request', 'Enviar solicitud')}
      </button>
      <button onClick={onBack}
              style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--textSecondary, #6b7280)', cursor: 'pointer' }}>
        {t('shared_video.back', 'Volver')}
      </button>
    </div>
  );
};


/** Action-row pill (like, dislike, emoji): `color` marks the active one. */
const pill = (active: boolean, color: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
  fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', fontVariantNumeric: 'tabular-nums',
  color: active ? color : 'var(--text-primary, #111827)',
  background: active ? `color-mix(in srgb, ${color} 12%, transparent)` : 'transparent',
  border: `1px solid ${active ? color : 'var(--border-color, #e5e7eb)'}`,
});

// Emoji reactions shown on the end-of-video overlay and below the player
const QUICK_EMOJIS = ['❤️', '👍', '🔥', '👏', '👀', '😮'] as const;

export interface ViewerProfile {
  email: string;
  name: string;
  isIdentified: boolean; // true = found in OrganizationUsers
  role?: string | null;
  photo?: string | null;
}

interface SharedVideoPageProps {
  shareToken: string;
}

const SharedVideoPage: React.FC<SharedVideoPageProps> = ({ shareToken }) => {
  const [video, setVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessRestricted, setAccessRestricted] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  // The owner's school is archived (Admin Center Archive Mode): the link doesn't work.
  const [archiveBlocked, setArchiveBlocked] = useState(false);
  // Data from 403 payload
  const [restrictedVisibility, setRestrictedVisibility] = useState<string>('org');
  // State machine: email (not signed in) | no-access (signed in, refused) -> form -> sent
  type RestrictedStep = 'email' | 'no-access' | 'form' | 'sent';
  const [restrictedStep, setRestrictedStep] = useState<RestrictedStep>('email');
  const [restrictedEmail, setRestrictedEmail] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const VISIBILITY_OPTIONS = useShareVisibilityOptions(14);
  // Viewer identity: set from localStorage/URL (Approach A) or lazily via email form (Approach B)
  const [viewerProfile, setViewerProfile] = useState<ViewerProfile | null>(null);
  const commentsSectionRef = useRef<HTMLDivElement>(null);
  // End-of-video overlay
  const [videoEnded, setVideoEnded] = useState(false);
  /** The player's <video>: comments read its time and seek it. */
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);
  const [emojiSent, setEmojiSent] = useState(false);
  // Registration prompt: shown ~3 s after the video loads if no identity was found
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get video reactions
  const {
    likes,
    dislikes,
    userReaction,
    toggling: reactionToggling,
    toggleReaction
  } = useVideoReactions(video?.id || '');

  // Emoji reaction summary (❤️ 3 · 🔥 2 …)
  const [emojiSummary, setEmojiSummary] = useState<{ emoji: string; count: number }[]>([]);
  useEffect(() => {
    if (!video?.id) return;
    apiClient.getEmojiReactionSummary(video.id)
      .then(r => { if (r.data) setEmojiSummary(r.data); })
      .catch(() => {});
  }, [video?.id]);

  // Signed-in viewers react with their email; anonymous viewers get a browser id (handled
  // in apiClient) — a fake email is never written as the viewer's identity.
  // One request at a time: rapid clicks on different emojis could otherwise resolve out of
  // order and leave the stored reaction different from the one shown as selected.
  const emojiInFlightRef = useRef(false);
  const sendEmojiReaction = async (emoji: string) => {
    if (!video?.id || emoji === selectedEmoji || emojiInFlightRef.current) return;
    emojiInFlightRef.current = true;
    const previous = selectedEmoji;
    setSelectedEmoji(emoji);
    setEmojiSent(false);
    const { data, error } = await apiClient
      .addEmojiReaction(video.id, emoji)
      .finally(() => { emojiInFlightRef.current = false; });
    if (error) {
      setSelectedEmoji(previous);
      toast.error(t('shared_video.reaction_error', "Couldn't save your reaction. Please try again."));
      return;
    }
    setEmojiSent(true);
    if (data?.summary) setEmojiSummary(data.summary);
  };

  const handleToggleReaction = (reactionType: 'like' | 'dislike') => {
    if (!getAccessToken()) {
      toast.warning(t('shared_video.like_requires_identity', 'Sign in to like or dislike this video.'));
      return;
    }
    toggleReaction(reactionType);
  };

  const handleBack = () => {
    navigate('/');
  };

  // Views are counted when playback starts (CustomVideoPlayer trackViews), not on page load.

  useEffect(() => {
    let isMounted = true;
    
    const fetchSharedVideo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Signed in = inside ACV with its sign-in token (the backend verifies it). Anyone
        // else is an anonymous viewer: public videos and reactions only, no comments.
        const storedEmail = localStorage.getItem('userEmail');
        const storedSlug  = localStorage.getItem('slug');
        if (getAccessToken() && storedEmail) apiClient.setUserContext(storedEmail, storedSlug || undefined);

        // Fetch the video data using the API client
        const { data, error } = await apiClient.getSharedVideo(shareToken);
        // Signed in only once the backend has accepted the token: a stale one (e.g. the link
        // opened in its own tab, outside ACV) is dropped by authFetch during this request and
        // the viewer continues anonymously — as in a fresh browser.
        if (isMounted && getAccessToken() && storedEmail) {
          setViewerProfile({ email: storedEmail, name: storedEmail.split('@')[0], isIdentified: true });
          setRestrictedEmail(storedEmail);
        }

        // Backend returns 403 with JSON { code, requiredVisibility, ownerSlug }
        // when the viewer does not meet the visibility requirements.
        if (error) {
          const { code, status, body } = error as Error & { code?: string; status?: number; body?: any };
          if (code === 'LINK_EXPIRED' || status === 410) {
            if (isMounted) { setLinkExpired(true); setLoading(false); }
            return;
          }
          if (code === 'ARCHIVE_ACCESS_BLOCKED') {
            if (isMounted) { setArchiveBlocked(true); setLoading(false); }
            return;
          }
          if (code === 'ACCESS_RESTRICTED' || status === 403) {
            if (isMounted) {
              if (body?.requiredVisibility) setRestrictedVisibility(body.requiredVisibility);
              setAccessRestricted(true);
              // Signed in and still refused: not allowed. Not signed in: sign in first.
              setRestrictedStep(getAccessToken() ? 'no-access' : 'email');
              setLoading(false);
            }
            return;
          }
          throw error;
        }
        if (!data) throw new Error('Video not found or not shared publicly');
        
        if (isMounted) {
          // Normalize the video data to handle both snake_case and camelCase properties
          const normalizedVideo = {
            ...data,
            isShared: data.isShared || data.is_shared || false,
            shareToken: data.shareToken || data.share_token || '',
            fileSize: data.fileSize || data.file_size || 0,
            createdAt: data.createdAt || data.created_at || new Date().toISOString(),
            thumbnailUrl: data.thumbnailUrl || data.thumbnail_url || '',
            fileUrl: data.file_url || data.url || '',
            streamUrl: data.streamUrl || ''
          };
          
          setVideo(normalizedVideo);
          
          // Fetch comment count
          const { data: comments } = await apiClient.listComments(data.id);
          
          setCommentCount(comments?.length || 0);
          setLoading(false);
          // Views are incremented by the useEffect below (single responsibility).
          // A second call here was the source of the double POST /view on every page load.
        }
      } catch (err) {
        console.error('Error in fetchSharedVideo:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load video');
          setLoading(false);
        }
      }
    };

    if (shareToken) {
      fetchSharedVideo();
    } else {
      setError('Invalid share link');
      setLoading(false);
    }
    
    return () => {
      isMounted = false;
    };
  }, [shareToken]);

  // A token rejected later (expired while the page was open) and not renewable: the viewer is
  // anonymous from here on, so the page stops offering what needs a sign-in.
  useEffect(() => {
    const onSignedOut = () => setViewerProfile(null);
    window.addEventListener('airec-signed-out', onSignedOut);
    return () => window.removeEventListener('airec-signed-out', onSignedOut);
  }, []);

  /** Fresh (short-lived) links for a page left open: the player resumes where it was. */
  const refreshLinks = async () => {
    if (!shareToken) return;
    const { data } = await apiClient.getSharedVideo(shareToken);
    if (!data) return;
    setVideo((prev) => (prev ? {
      ...prev,
      playbackUrl: data.playbackUrl,
      streamUrl: data.streamUrl || prev.streamUrl,
      thumbnailUrl: data.thumbnailUrl || prev.thumbnailUrl,
    } : prev));
  };

  // Show registration prompt 3 s after video loads, only when user has no identity
  useEffect(() => {
    if (!video) return;
    if (viewerProfile !== null) return; // already identified
    const timer = setTimeout(() => setShowRegisterPrompt(true), 3000);
    return () => clearTimeout(timer);
  }, [video?.id, viewerProfile]);

  useEffect(() => {
    if (!video || window.location.hash !== '#comments') return;
    const timer = window.setTimeout(() => {
      commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [video?.id]);


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs      = Math.abs(now.getTime() - date.getTime());
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours   = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays    = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1)   return t('time_units.just_now');
    if (diffHours   < 1)   return diffMinutes === 1
      ? t('time_units.minute_ago')
      : t('time_units.minutes_ago', { count: diffMinutes });
    if (diffDays    < 1)   return diffHours === 1
      ? t('time_units.hour_ago')
      : t('time_units.hours_ago', { count: diffHours });
    if (diffDays    === 1) return t('time_units.day_ago');
    if (diffDays    < 30)  return t('time_units.days_ago', { count: diffDays });
    if (diffDays    < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1
        ? t('time_units.month_ago')
        : t('time_units.months_ago', { count: months });
    }
    const years = Math.floor(diffDays / 365);
    return years === 1
      ? t('time_units.year_ago')
      : t('time_units.years_ago', { count: years });
  };

  // Non-owners get the video without the owner's email, so both sides must be present
  // (otherwise undefined === undefined made every anonymous viewer the "owner").
  const ownerEmail = String((video as any)?.ownerEmail || video?.userEmail || '').toLowerCase();
  const isOwner = !!ownerEmail && viewerProfile?.email?.toLowerCase() === ownerEmail;

  const handleVisibilityChange = async (visibility: ShareVisibility) => {
    if (!video) return;
    setSavingVisibility(true);
    const { data } = await apiClient.patchShareVisibility(video.id, visibility);
    if (data) setVideo(prev => prev ? { ...prev, shareVisibility: visibility } : prev);
    setSavingVisibility(false);
  };

  const copyShareLink = async () => {
    if (await copyText(window.location.href)) toast.success(t('shared_video.link_copied'));
    else toast.error(t('share_link_copy_failed', "Couldn't copy the link. Please try again."));
  };

  const scrollToComments = () => {
    if (commentsSectionRef.current) {
      setTimeout(() => {
        commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  if (loading) {
    return (
      <SharedVideoLayout onBack={handleBack} guestViewer={viewerProfile}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="t-muted">{t('shared_video.loading')}</p>
          </div>
        </div>
      </SharedVideoLayout>
    );
  }


  // ── Handler: submit the access request form ──────────────────────────────
  const handleRequestAccess = async (name: string, message: string) => {
    setRequestSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/shared/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shareToken,
          name:    name.trim(),
          email:   restrictedEmail.trim(),
          message: message.trim() || null,
        }),
      });

      if (res.status === 429) {
        // Rate-limited — silently treat as success (request was already sent recently)
        setRestrictedStep('sent');
        return;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg  = (body as any)?.message || `Error ${res.status}`;
        toast.error(`${t('shared_video.request_error', 'No se pudo enviar la solicitud.')} (${msg})`);
        return;
      }

      setRestrictedStep('sent');
    } catch {
      toast.error(t('shared_video.request_error', 'No se pudo enviar la solicitud. Inténtalo de nuevo.'));
    } finally {
      setRequestSubmitting(false);
    }
  };

  if (accessRestricted) {
    // Card shell — shared across all steps
    const card = (icon: React.ReactNode, title: string, subtitle: string, children: React.ReactNode) => (
      <SharedVideoLayout onBack={handleBack} guestViewer={viewerProfile}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: 24 }}>
          <div style={{
            maxWidth: 460, width: '100%', padding: '40px 36px', borderRadius: 16,
            background: 'var(--card, #fff)', border: '1px solid var(--border, #e5e7eb)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%', margin: '0 auto 20px',
              background: 'color-mix(in srgb, var(--primary, #4f46e5) 10%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {icon}
            </div>
            <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: 'var(--foreground, #111)', textAlign: 'center' }}>
              {title}
            </h2>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--textSecondary, #6b7280)', lineHeight: 1.6, textAlign: 'center' }}>
              {subtitle}
            </p>
            {children}
          </div>
        </div>
      </SharedVideoLayout>
    );

    // ── Step: not signed in ────────────────────────────────────────────────
    // Organisation / network videos need a verified ACV sign-in (the backend checks the
    // sign-in token); an email typed here only addresses an access request to the owner.
    if (restrictedStep === 'email') {
      return card(
        <Lock size={26} style={{ color: 'var(--primary-text, #4f46e5)' }} aria-hidden />,
        t('shared_video.restricted_title', 'Acceso restringido'),
        restrictedVisibility === 'private'
          ? t('shared_video.restricted_private_desc', 'Este video es privado. Solo el propietario puede verlo.')
          : t('shared_video.restricted_signin_desc', 'Este video es solo para miembros de la organización. Inicia sesión en ACV para verlo, o solicita acceso al propietario.'),
        restrictedVisibility === 'private'
          ? (
            <div style={{ textAlign: 'center', color: 'var(--textSecondary, #6b7280)', fontSize: 13 }}>
              {t('shared_video.private_ask_owner', 'Solicita al propietario del video que cambie su visibilidad o te comparta un enlace público.')}
            </div>
          )
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <a
                href="https://portal.acv.app"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '11px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: 'var(--primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)', textDecoration: 'none',
                }}
              >
                <ExternalLink size={14} aria-hidden />
                {t('shared_video.login_acv', 'Iniciar sesión en ACV App')}
              </a>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground, #111)', marginTop: 8 }}
                     htmlFor="ra_email">
                {t('shared_video.request_email_label', 'O solicita acceso con tu correo')}
              </label>
              <input
                id="ra_email"
                type="email"
                value={restrictedEmail}
                onChange={e => setRestrictedEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && restrictedEmail.trim() && setRestrictedStep('form')}
                placeholder={t('shared_video.email_placeholder', 'name@institution.edu')}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 14,
                  border: '1px solid var(--border, #d1d5db)',
                  background: 'var(--background, #fff)', color: 'var(--foreground, #111)',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => setRestrictedStep('form')}
                disabled={!restrictedEmail.trim()}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: 'transparent', color: 'var(--primary-text, #4f46e5)',
                  border: '1px solid var(--primary, #4f46e5)',
                  cursor: restrictedEmail.trim() ? 'pointer' : 'not-allowed', opacity: restrictedEmail.trim() ? 1 : 0.6,
                }}
              >
                <Send size={14} aria-hidden /> {t('shared_video.request_access_btn', 'Solicitar acceso')}
              </button>
            </div>
          ),
      );
    }

    // ── Step: signed in but not allowed → offer request access ────────────
    if (restrictedStep === 'no-access') {
      return card(
        <Lock size={26} style={{ color: 'var(--primary-text, #4f46e5)' }} aria-hidden />,
        t('shared_video.no_access_title', 'No tienes acceso a este video'),
        t('shared_video.not_in_org_desc', 'Para ver este video, puedes solicitar acceso al propietario. Él recibirá un correo con tu solicitud.'),
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={() => setRestrictedStep('form')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '11px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
              background: 'var(--primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)', border: 'none', cursor: 'pointer',
            }}
          >
            <Send size={14} aria-hidden />
            {t('shared_video.request_access_btn', 'Solicitar acceso')}
          </button>
        </div>,
      );
    }

    // ── Step: request access form ─────────────────────────────────────────
    // AccessRequestForm is a separate component so its useEffect([], []) fires
    // exactly once on mount — regardless of parent re-renders.
    if (restrictedStep === 'form') {
      return card(
        <Send size={26} style={{ color: 'var(--primary-text, #4f46e5)' }} aria-hidden />,
        t('shared_video.request_form_title', 'Solicitar acceso al video'),
        t('shared_video.request_form_subtitle', 'Completa el formulario y el propietario del video recibirá tu solicitud por correo.'),
        <AccessRequestForm
          email={restrictedEmail}
          onSubmit={handleRequestAccess}
          onBack={() => setRestrictedStep(getAccessToken() ? 'no-access' : 'email')}
          submitting={requestSubmitting}
        />,
      );
    }

    // ── Step: confirmation ────────────────────────────────────────────────
    return card(
      <CheckCircle size={26} style={{ color: '#22c55e' }} aria-hidden />,
      t('shared_video.request_sent_title', 'Solicitud enviada'),
      t('shared_video.request_sent_desc', 'El propietario del video recibirá tu solicitud por correo. Te avisaremos si te conceden acceso.'),
      <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--textSecondary, #6b7280)' }}>
        {t('shared_video.request_sent_note', 'Si el propietario aprueba tu solicitud, podrás ver el video cuando te compartan un enlace actualizado.')}
      </div>,
    );
  }

  if (error || !video) {
    return (
      <SharedVideoLayout onBack={handleBack} guestViewer={viewerProfile}>
        <div className="flex items-center justify-center h-full p-4">
          <div className="text-center">
            <MessageSquare className="w-16 h-16 t-muted mx-auto mb-4" />
            <h2 className="text-xl font-semibold t-text mb-2">
              {archiveBlocked ? t('archive.shared_blocked_title', "This video isn't available")
                : linkExpired ? t('shared_video.expired_title', 'Link expired') : t('shared_video.not_found_title')}
            </h2>
            <p className="t-muted mb-6">
              {archiveBlocked ? t('archive.shared_blocked_desc', 'The school that shared it no longer has access to AI Recording.')
                : linkExpired ? t('shared_video.expired_message', 'This share link has expired. Ask the owner for a new link.') : t('shared_video.not_found_message')}
            </p>
          </div>
        </div>
      </SharedVideoLayout>
    );
  }

  // Received emoji reactions (a list; anything else counts as none).
  const emojiCounts = Array.isArray(emojiSummary) ? emojiSummary : [];

  return (
    <SharedVideoLayout onBack={handleBack} guestViewer={viewerProfile}>
      {/* Video with its details on the left; comments beside it on wide screens (stacked on narrow). */}
      <div className="airec-share max-w-7xl mx-auto w-full">
        <div className="min-w-0">
          {/* Registration prompt — shown when viewer has no identity */}
          {showRegisterPrompt && !viewerProfile && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '12px 16px', marginBottom: 16, borderRadius: 10,
              background: 'color-mix(in srgb, var(--theme-primary, #4f46e5) 8%, #fff)',
              border: '1px solid color-mix(in srgb, var(--theme-primary, #4f46e5) 25%, transparent)',
            }}>
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #111)' }}>
                  {t('shared_video.register_prompt_title', '¿Quieres dejar tu huella?')}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
                  {t('shared_video.register_prompt_desc', 'Inicia sesión en ACV para comentar, o regístrate si aún no tienes cuenta.')}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {/* Comments need a verified ACV sign-in (no anonymous comments). */}
                <a
                  href="https://portal.acv.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowRegisterPrompt(false)}
                  style={{
                    display: 'flex', alignItems: 'center',
                    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                    border: '1px solid var(--theme-border, #e5e7eb)',
                    background: 'var(--card-bg, #fff)', color: 'var(--text-primary, #111)', textDecoration: 'none',
                  }}
                >
                  {t('shared_video.login_acv', 'Iniciar sesión en ACV App')}
                </a>
                <a
                  href="https://www.acv.app/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowRegisterPrompt(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: 'var(--theme-primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)',
                    textDecoration: 'none', cursor: 'pointer',
                  }}
                >
                  <ExternalLink size={12} aria-hidden />
                  {t('shared_video.register_cta', 'Registrarse en ACV')}
                </a>
                <button
                  onClick={() => setShowRegisterPrompt(false)}
                  aria-label={t('close', 'Close')}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 16, color: 'var(--text-secondary, #9ca3af)', padding: '0 4px',
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Video Player */}
          <div className="w-full aspect-video mb-4 bg-black rounded-lg overflow-hidden" style={{ position: 'relative' }}>
            <CustomVideoPlayer
              src={video.playbackUrl || video.streamUrl || video.file_url || video.url || ''}
              poster={video.thumbnail_url || video.thumbnailUrl}
              mimeType={video.mimeType || 'video/webm'}
              knownDuration={video.duration}
              videoId={video.id}
              onEnded={() => setVideoEnded(true)}
              onPlay={() => setVideoEnded(false)}
              onSourceError={() => void refreshLinks()}
              trackViews
              videoElementRef={playerRef}
              style={{ width: '100%', height: '100%' }}
            />

            {/* End-of-video overlay — Loom-style */}
            {videoEnded && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(15,15,20,0.82)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 24,
                borderRadius: 8,
                backdropFilter: 'blur(4px)',
              }}>
                {/* Emoji reaction bar */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {QUICK_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => sendEmojiReaction(emoji)}
                        title={emoji}
                        style={{
                          fontSize: selectedEmoji === emoji ? 32 : 26,
                          lineHeight: 1,
                          background: selectedEmoji === emoji
                            ? 'rgba(255,255,255,0.2)'
                            : 'rgba(255,255,255,0.08)',
                          border: selectedEmoji === emoji
                            ? '2px solid rgba(255,255,255,0.5)'
                            : '2px solid transparent',
                          borderRadius: '50%',
                          width: 52, height: 52,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        aria-label={emoji}
                        aria-pressed={selectedEmoji === emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  {/* Confirmation feedback — shown briefly after the reaction is persisted */}
                  {emojiSent && selectedEmoji && (
                    <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.7)', animation: 'fadeIn 0.3s ease' }}>
                      {t('shared_video.reaction_sent', '¡Gracias por tu reacción!')}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={() => {
                      setVideoEnded(false);
                      const vid = playerRef.current;
                      if (vid) { vid.currentTime = 0; void vid.play().catch(() => {}); }
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                      background: 'rgba(255,255,255,0.12)', color: '#fff',
                      border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                  >
                    <RotateCcw size={16} aria-hidden />
                    {t('shared_video.watch_again', 'Ver de nuevo')}
                  </button>
                  <button
                    onClick={() => { setVideoEnded(false); scrollToComments(); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                      background: 'var(--theme-primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)',
                      border: 'none', cursor: 'pointer',
                      transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <MessageCircle size={16} aria-hidden />
                    {t('shared_video.leave_comment', 'Dejar un comentario')}
                  </button>
                </div>

                {/* Creator name */}
                {video.user_id && (
                  <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                    {t('shared_video.reply_to', 'Responder a')} {video.user_id}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Title, details and actions — the title shows once, and there is one Share. */}
          <h1 className="text-xl sm:text-2xl font-bold t-text break-words">{video.title}</h1>
          <p className="text-sm t-muted mt-1">
            {[
              formatDate(video.createdAt || video.created_at),
              Number(video.views) > 0 ? t('library_card.views', { count: Number(video.views) }) : null,
            ].filter(Boolean).join(' · ')}
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button
              onClick={() => handleToggleReaction('like')}
              disabled={reactionToggling}
              aria-pressed={userReaction === 'like'}
              aria-label={t('shared_video.like', 'Like')}
              style={pill(userReaction === 'like', 'var(--primary-text, #4f46e5)')}
            >
              <ThumbsUp className={`w-4 h-4 ${userReaction === 'like' ? 'fill-current' : ''}`} aria-hidden />
              {likes > 0 ? likes : t('shared_video.like')}
            </button>
            <button
              onClick={() => handleToggleReaction('dislike')}
              disabled={reactionToggling}
              aria-pressed={userReaction === 'dislike'}
              aria-label={t('shared_video.dislike', 'Dislike')}
              style={pill(userReaction === 'dislike', 'var(--theme-destructive, #dc2626)')}
            >
              <ThumbsDown className={`w-4 h-4 ${userReaction === 'dislike' ? 'fill-current' : ''}`} aria-hidden />
              {dislikes > 0 ? dislikes : null}
            </button>
            {/* Emoji reactions, with how many each has received. */}
            {QUICK_EMOJIS.map((emoji) => {
              const count = emojiCounts.find((e) => e.emoji === emoji)?.count ?? 0;
              return (
                <button
                  key={emoji}
                  onClick={() => sendEmojiReaction(emoji)}
                  title={emoji}
                  aria-label={emoji}
                  aria-pressed={selectedEmoji === emoji}
                  style={{ ...pill(selectedEmoji === emoji, 'var(--primary-text, #4f46e5)'), padding: count ? '4px 10px 4px 8px' : '4px 8px' }}
                >
                  <span style={{ fontSize: 17, lineHeight: 1 }}>{emoji}</span>
                  {count > 0 && count}
                </button>
              );
            })}
            {emojiCounts.filter(({ emoji }) => !(QUICK_EMOJIS as readonly string[]).includes(emoji)).map(({ emoji, count }) => (
              <span key={emoji} style={{ ...pill(false, ''), cursor: 'default' }}>
                <span style={{ fontSize: 17, lineHeight: 1 }}>{emoji}</span>{count}
              </span>
            ))}
            <button
              onClick={copyShareLink}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
              style={{
                marginLeft: 'auto',
                backgroundColor: 'var(--button-primary-bg, var(--theme-primary, #4f46e5))',
                color: 'var(--button-primary-foreground, #ffffff)',
              }}
            >
              <Share2 className="w-4 h-4" aria-hidden />
              {t('shared_video.share_button')}
            </button>
          </div>
          {emojiSent && selectedEmoji && (
            <p className="text-xs t-muted mt-2">{t('shared_video.reaction_sent', 'Thanks for your reaction!')}</p>
          )}

          {video.description && (
            <div className="rounded-lg p-4 mt-4" style={{ background: 'color-mix(in srgb, var(--text-secondary, #6b7280) 8%, transparent)' }}>
              <RichText value={video.description} className="t-text text-sm" />
            </div>
          )}

          {/* Share Visibility Control — only visible to the video owner */}
          {isOwner && (
            <div style={{
              background: 'var(--card, #fff)',
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: 12, padding: '14px 16px', marginTop: 16,
            }}>
              <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--foreground, #111)' }}>
                {t('shared_video.visibility_label', 'Visibilidad del link compartido')}
                {savingVisibility && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--textSecondary, #9ca3af)', fontWeight: 400 }}>
                    {t('shared_video.saving', 'Guardando…')}
                  </span>
                )}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {VISIBILITY_OPTIONS.map(opt => {
                  const active = (video.shareVisibility ?? 'public') === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleVisibilityChange(opt.value)}
                      disabled={savingVisibility}
                      title={opt.desc}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: active ? 600 : 400,
                        cursor: 'pointer', transition: 'all 0.15s',
                        background: active ? 'var(--theme-primary, #4f46e5)' : 'var(--controlsBg, #f3f4f6)',
                        color: active ? 'var(--button-primary-foreground, #fff)' : 'var(--foreground, #374151)',
                        border: active ? '1px solid transparent' : '1px solid var(--border, #e5e7eb)',
                        opacity: savingVisibility ? 0.6 : 1,
                      }}
                      aria-pressed={active}
                    >
                      {opt.icon}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--textSecondary, #9ca3af)' }}>
                {VISIBILITY_OPTIONS.find(o => o.value === (video.shareVisibility ?? 'public'))?.desc}
              </p>
            </div>
          )}
        </div>

        {/* Comments — the same thread as in the app (comments are keyed by video id). */}
        <aside ref={commentsSectionRef} className="airec-share__comments t-surface rounded-lg border t-border">
          <h2 className="text-base font-semibold t-text mb-4">
            {t('shared_video.comments')}
            {commentCount > 0 && <span className="t-muted font-normal"> · {commentCount}</span>}
          </h2>
          <Comments
            videoId={video.id}
            playerRef={playerRef}
            onCommentAdded={() => setCommentCount(prev => prev + 1)}
            onCommentDeleted={() => setCommentCount(prev => Math.max(0, prev - 1))}
            viewerProfile={viewerProfile}
            videoSlug={
              // Prefer ownerSlug stored in the video document; fall back to
              // the viewer's own slug from localStorage (ACV App postMessage)
              // or URL param — in the ACV ecosystem the viewer is always in the
              // same tenant as the video owner when accessing through the iframe.
              (video as any).ownerSlug
                || localStorage.getItem('slug')
                || new URLSearchParams(window.location.search).get('slug')
                || undefined
            }
          />
        </aside>
      </div>
    </SharedVideoLayout>
  );
};

export default SharedVideoPage;